import React, { useRef } from 'react';
import { ScissorsIcon, LayersIcon, SmileIcon, SparklesIcon, UsersIcon, PaletteIcon } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { useLanguage, TRANSLATIONS } from '../../context/LanguageContext';
type ServiceCardProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
};
function ServiceCard({
  icon,
  title,
  description,
  delay
}: ServiceCardProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5,
    delay
  }} className="group relative bg-[#1c1c1c] border border-[#2a2a2a] rounded-lg p-6 hover:border-[#c41e3a]/50 transition-all duration-300" data-id="element-256">
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#c41e3a] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-t-lg" aria-hidden="true" data-id="element-257" />
      <div className="flex items-start gap-4" data-id="element-258">
        <div className="flex-shrink-0 w-12 h-12 bg-[#c41e3a]/10 border border-[#c41e3a]/20 rounded-lg flex items-center justify-center text-[#c41e3a]" data-id="element-259">
          {icon}
        </div>
        <div data-id="element-260">
          <h3 className="text-white font-bold text-xl mb-1" style={{
          fontFamily: 'Oswald, sans-serif',
          letterSpacing: '0.04em'
        }} data-id="element-261">
            {title}
          </h3>
          <p className="text-gray-400 text-sm leading-relaxed" data-id="element-262">{description}</p>
        </div>
      </div>
    </motion.div>;
}
export function Services() {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, {
    once: true,
    margin: '-60px'
  });
  const {
    language
  } = useLanguage();
  const t = TRANSLATIONS[language || 'en'];
  const services = [{
    icon: <ScissorsIcon size={22} data-id="element-263" />,
    title: t.haircut_name,
    description: t.haircut_desc
  }, {
    icon: <LayersIcon size={22} data-id="element-264" />,
    title: t.fade_name,
    description: t.fade_desc
  }, {
    icon: <SmileIcon size={22} data-id="element-265" />,
    title: t.beard_name,
    description: t.beard_desc
  }, {
    icon: <SparklesIcon size={22} data-id="element-266" />,
    title: t.shave_name,
    description: t.shave_desc
  }, {
    icon: <UsersIcon size={22} data-id="element-267" />,
    title: t.kids_name,
    description: t.kids_desc
  }, {
    icon: <PaletteIcon size={22} data-id="element-268" />,
    title: t.combo_name,
    description: t.combo_desc
  }];
  return <section id="services" className="w-full bg-[#0d0d0d] py-20 md:py-28 px-4" data-id="element-269">
      <div className="max-w-6xl mx-auto" data-id="element-270">
        <motion.div ref={headerRef} initial={{
        opacity: 0,
        y: 30
      }} animate={headerInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-271">
          <p className="text-[#c41e3a] font-semibold tracking-[0.25em] uppercase text-sm mb-3" style={{
          fontFamily: 'Oswald, sans-serif'
        }} data-id="element-272">
            {t.services_eyebrow}
          </p>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-4" style={{
          fontFamily: 'Oswald, sans-serif'
        }} data-id="element-273">
            {t.services_headline}
          </h2>
          <div className="section-divider w-24 mx-auto" data-id="element-274" />
          <p className="text-gray-400 mt-6 text-lg max-w-xl mx-auto" data-id="element-275">
            {t.services_subtext}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-276">
          {services.map((s, i) => <ServiceCard key={i} icon={s.icon} title={s.title} description={s.description} delay={i * 0.08} data-id="element-277" />)}
        </div>
      </div>
    </section>;
}